import type { CartItem } from '@stores/useCartStore'
import { calculateItemPrice, calculateItemRelatedOriginalPrice } from '@utils/currency'
import type { CrossSellProduct, PortalRelatedProduct } from '@/types'

export interface CrossSellTotals {
  itemCount: number
  subtotal: number
  savings: number
  savingsPercent: number
}

// Mirrors the admin's cross-sell column rule: four 288px cards (+ 3 x 16px gaps = 1200px) fit only
// at the widest Maximum Content Width (the editor's slider tops out at 1200px), three otherwise.
export const CROSS_SELL_WIDE_CONTENT_WIDTH = 1200
export const CROSS_SELL_MAX_COLUMNS = 4
export const CROSS_SELL_NARROW_MAX_COLUMNS = 3

// Same fallback as the marketplace product page when a product sets no limit.
export const DEFAULT_MAX_QUANTITY = 10

export function crossSellMaxColumns(maxContentWidth: number | string | undefined): number {
  const width = parseFloat(String(maxContentWidth ?? ''))
  return width >= CROSS_SELL_WIDE_CONTENT_WIDTH ? CROSS_SELL_MAX_COLUMNS : CROSS_SELL_NARROW_MAX_COLUMNS
}

function toNumber(value: number | string | null | undefined): number {
  const parsed = parseFloat(String(value ?? ''))
  return Number.isFinite(parsed) ? parsed : 0
}

// The admin can link the same target more than once; like legacy, the first row per target wins.
export function toCrossSellProducts(rows: PortalRelatedProduct[] | undefined): CrossSellProduct[] {
  const seen = new Set<number>()
  const products: CrossSellProduct[] = []
  ;(rows ?? []).forEach(row => {
    const target = row.to_campaign_product
    if (!target || seen.has(target.id)) {
      return
    }
    seen.add(target.id)
    const subtext = row.subtext?.length ? row.subtext : target.subtext
    products.push({
      id: row.id,
      campaign_product: target,
      variant: row.to_variant,
      name: row.display_name || target.name,
      sub_line: subtext?.length ? subtext.join(' • ') : undefined,
      description: target.product?.description || undefined,
      sales_phrase: row.sales_phrase?.text || undefined,
      price: toNumber(row.portal_price),
      regular_price: toNumber(row.to_variant?.price ?? target.price),
      discount_percent: Math.round(toNumber(row.portal_discount)),
      currency: target.currency,
      max_quantity: target.max_quantity || DEFAULT_MAX_QUANTITY,
    })
  })
  return products
}

// "Up to X% off" — the best discount in the list.
export function maxDiscountPercent(products: CrossSellProduct[]): number {
  return products.reduce((max, product) => Math.max(max, product.discount_percent), 0)
}

// The backend prices the line from `related_product`; the price fields here only drive what the
// cart shows until the sync response replaces the line. Always a one-time purchase, as the checkout
// drawer sends it: in OMP `buy_now_clicked: false` means "add to the subscription".
export function toCartLine(product: CrossSellProduct, quantity: number): CartItem {
  return {
    campaign_product: product.campaign_product,
    variant: product.variant,
    quantity,
    buy_now_clicked: true,
    related_product: {
      id: product.id,
      price: product.price,
      discount: Math.max(0, product.regular_price - product.price),
      discount_percent: product.discount_percent,
    },
  }
}

// A line the cross-sell cart owns: one priced from a related-product offer.
export function isCrossSellLine(item: CartItem): boolean {
  return !!item.related_product
}

export function quantityInCart(cart: CartItem[], productId: number): number {
  return cart.reduce((sum, item) => (item.related_product?.id === productId ? sum + item.quantity : sum), 0)
}

// Line totals, priced the way the marketplace cart prices them: a related line is
// `related_product.price × quantity`, struck at the regular price when it carries a discount.
export function cartLineTotal(item: CartItem): { price: number; original: number | null } {
  return { price: calculateItemPrice(item), original: calculateItemRelatedOriginalPrice(item) }
}

// Shipping and tax are added at checkout, so these totals exclude them (the drawer says so).
export function cartTotals(cart: CartItem[]): CrossSellTotals {
  let itemCount = 0
  let subtotal = 0
  let savings = 0
  cart.forEach(item => {
    const { price, original } = cartLineTotal(item)
    itemCount += item.quantity
    subtotal += price
    if (original != null) {
      savings += Math.max(0, original - price)
    }
  })
  const savingsPercent = subtotal + savings > 0 ? Math.round((savings / (subtotal + savings)) * 100) : 0
  return { itemCount, subtotal, savings, savingsPercent }
}

export function campaignProductIdOf(item: any): number | undefined {
  const campaignProduct = item?.campaign_product
  return typeof campaignProduct === 'object' ? campaignProduct?.id : campaignProduct
}
